// Configurações fiscais do cliente: emissor, CSC da NFC-e, situação da integração fiscal,
// certificado digital (upload; só status e validade ficam visíveis) e regras por NCM.
import React, { useEffect, useRef, useState } from "react";
import { caminhoCertificado, caminhoEmpresa, fiscal } from "../api";
import CertificadoDigital from "../components/CertificadoDigital";
import { useDocumentos } from "../components/DocumentoLiberado";
import { Aviso, Botao, Campo, Cartao, Entrada, Numero, Selecao, Tabela, Valor } from "../components/ui";
import { dataBR, documentoValido, mascaraDocumento, REGIMES, soDigitos, UFS } from "../utils";

const VAZIO = {
  cnpj: "", razao_social: "", nome_fantasia: "", inscricao_estadual: "", inscricao_municipal: "",
  regime_tributario: "1", email: "", ambiente: "homologacao", serie_nfe: 1, serie_nfce: 1, incluir_ibs_cbs: false,
  endereco: { logradouro: "", numero: "", complemento: "", bairro: "", codigo_municipio: "", municipio: "", uf: "SC", cep: "", telefone: "" },
  nfce_id_csc: "", nfce_csc: "",
  nfse_serie_dps: "1", nfse_numero_inicial_dps: "", nfse_regime_especial: 0, nfse_reg_ap_trib_sn: 1,
  nfse_incentivo_fiscal: false, nfse_aliquota_sn: "", nfse_prefeitura_login: "", nfse_prefeitura_senha: "",
};

export default function Configuracao() {
  const [e, setE] = useState(VAZIO);
  // Produção (com validade fiscal) só com plano pago ativo ou liberação do Admin Master; a trava fica no servidor
  // Começa indisponível até o servidor responder; se a consulta falhar, continua indisponível e avisa
  const [prod, setProd] = useState({ liberado: false, carregando: true });
  useEffect(() => {
    fiscal.producao().then(setProd)
      .catch(() => setProd({ liberado: false, erro: true, mensagem: "Não foi possível conferir se a produção está liberada para esta empresa. Recarregue a página; até lá, use a homologação." }));
  }, []);
  const [meta, setMeta] = useState({});
  const [msg, setMsg] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [emissorSalvo, setEmissorSalvo] = useState(false);
  const [consultaCnpj, setConsultaCnpj] = useState(null); // {estado: "buscando"|"ok"|"erro", texto}
  const cnpjConsultado = useRef("");
  const docs = useDocumentos();
  // fail-closed na tela (auditoria do PR #9): só mostra os campos de NFC-e quando o servidor CONFIRMA que está liberada;
  // carregando ou com erro, fica o aviso. A barreira real continua no servidor.
  const nfceLiberada = docs?.nfce?.liberado === true;

  useEffect(() => {
    fiscal.emissor().then((d) => {
      if (!d) return;
      setEmissorSalvo(!(d.empresa_pendencias || []).some((x) => x.startsWith("parâmetros fiscais")));
      cnpjConsultado.current = d.cnpj;  // empresa já cadastrada: não sobrescreve com a Receita
      const { nfce_csc_configurado, nfse_prefeitura_senha_configurada, ...resto } = d;
      setMeta({ nfce_csc_configurado, nfse_prefeitura_senha_configurada, ...metaSync(d) });
      setE({ ...VAZIO, ...resto, endereco: { ...VAZIO.endereco, ...resto.endereco },
        nfce_id_csc: resto.nfce_id_csc || "",
        nfse_numero_inicial_dps: resto.nfse_numero_inicial_dps ?? "", nfse_aliquota_sn: resto.nfse_aliquota_sn ?? "",
        nfse_prefeitura_login: resto.nfse_prefeitura_login || "", nfse_prefeitura_senha: "" });
    }).catch((err) => setMsg({ tipo: "erro", texto: err.message }));
  }, []);

  const set = (c, v) => setE((x) => ({ ...x, [c]: v }));
  const setEnd = (c, v) => setE((x) => ({ ...x, endereco: { ...x.endereco, [c]: v } }));

  // Ao completar um CNPJ válido, busca os dados na Receita e preenche os campos que estiverem vazios
  useEffect(() => {
    const cnpj = soDigitos(e.cnpj);
    if (cnpj.length !== 14 || !documentoValido(cnpj) || cnpj === cnpjConsultado.current) return;
    cnpjConsultado.current = cnpj;
    setConsultaCnpj({ estado: "buscando", texto: "Consultando a Receita Federal…" });
    fiscal.consultaCnpj(cnpj).then((r) => {
      const vazio = (v) => v === undefined || v === null || String(v).trim() === "";
      const end = r.endereco || {};
      setE((x) => {
        const novoEnd = { ...x.endereco };
        Object.entries(end).forEach(([k, v]) => { if (!vazio(v) && vazio(x.endereco[k])) novoEnd[k] = v; });
        if (end.uf) novoEnd.uf = end.uf;  // UF vem sempre da Receita (o campo já começa preenchido)
        return {
          ...x,
          razao_social: vazio(x.razao_social) ? (r.nome || "") : x.razao_social,
          nome_fantasia: vazio(x.nome_fantasia) ? (r.nome_fantasia || "") : x.nome_fantasia,
          inscricao_estadual: vazio(x.inscricao_estadual) ? (r.inscricao_estadual || "") : x.inscricao_estadual,
          email: vazio(x.email) ? (r.email || "") : x.email,
          regime_tributario: r.optante_mei ? "4" : r.optante_simples ? "1" : x.regime_tributario,
          endereco: novoEnd,
        };
      });
      const situacao = r.situacao_cadastral && !/ativa/i.test(r.situacao_cadastral) ? ` Situação na Receita: ${r.situacao_cadastral}.` : "";
      setConsultaCnpj({ estado: situacao ? "erro" : "ok",
        texto: `Dados preenchidos pela Receita Federal. Confira tudo e preencha ${r.inscricao_estadual ? "a inscrição municipal, que não vem" : "as inscrições estadual e municipal, que não vêm"} da Receita.${situacao}` });
    }).catch((err) => setConsultaCnpj({ estado: "erro", texto: err.status === 404 ? "CNPJ não encontrado na Receita Federal." : `Não foi possível consultar o CNPJ: ${err.message}` }));
  }, [e.cnpj]);

  const salvar = async () => {
    setSalvando(true);
    setMsg(null);
    try {
      const corpo = { ...e, cnpj: soDigitos(e.cnpj), email: e.email || null,
        nfce_csc: e.nfce_csc || null,
        inscricao_municipal: e.inscricao_municipal || null,
        nfse_numero_inicial_dps: e.nfse_numero_inicial_dps === "" ? null : Number(e.nfse_numero_inicial_dps),
        nfse_aliquota_sn: e.nfse_aliquota_sn === "" ? null : Number(e.nfse_aliquota_sn),
        nfse_prefeitura_login: e.nfse_prefeitura_login || null, nfse_prefeitura_senha: e.nfse_prefeitura_senha || null };
      if (meta.fonte_empresa) {
        // Dados da empresa vêm de "Minha empresa": aqui só vão os parâmetros fiscais
        ["cnpj", "razao_social", "nome_fantasia", "inscricao_estadual", "inscricao_municipal",
          "regime_tributario", "email", "endereco"].forEach((k) => delete corpo[k]);
      }
      const r = await fiscal.salvarEmissor(corpo);
      setMeta((m) => ({ ...m, nfce_csc_configurado: r.nfce_csc_configurado,
        nfse_prefeitura_senha_configurada: r.nfse_prefeitura_senha_configurada, ...metaSync(r) }));
      setE((x) => ({ ...x, nfce_csc: "", nfse_prefeitura_senha: "",
        nfse_numero_inicial_dps: r.nfse_numero_inicial_dps ?? "" }));
      setEmissorSalvo(true);
      setMsg(r.sincronizacao_erro
        ? { tipo: "atencao", texto: `Dados salvos, mas a integração fiscal ainda não aceitou a empresa: ${r.sincronizacao_erro}` }
        : { tipo: "sucesso", texto: "Dados salvos e empresa cadastrada na integração fiscal. Agora envie o certificado digital (abaixo)." });
    } catch (err) {
      setMsg({ tipo: "erro", texto: err.message });
    } finally {
      setSalvando(false);
    }
  };

  const recarregarSync = async () => {
    const atual = await fiscal.emissor().catch(() => null);
    if (!atual) return;
    setMeta((m) => ({ ...m, ...metaSync(atual) }));
    setE((x) => ({ ...x, nfse_numero_inicial_dps: atual.nfse_numero_inicial_dps ?? "" }));
  };

  const sincronizar = async () => {
    setSincronizando(true);
    setMsg(null);
    try {
      const r = await fiscal.sincronizar();
      // o "próximo nº de DPS" vale uma vez só: recarrega o valor do servidor (limpo depois de aplicado)
      await recarregarSync();
      setMsg(r.aviso_nfse
        ? { tipo: "atencao", texto: `NF-e e NFC-e prontas. NFS-e com pendência: ${r.aviso_nfse}` }
        : { tipo: "sucesso", texto: "Empresa, certificado e configurações (NF-e, NFC-e e NFS-e) atualizados na integração fiscal." });
    } catch (err) {
      await recarregarSync();
      setMsg({ tipo: "erro", texto: err.message });
    } finally {
      setSincronizando(false);
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Configurações fiscais</h1>

      {meta.fonte_empresa ? <EmpresaDaFonte e={e} pendencias={meta.empresa_pendencias} /> : (
      <Cartao titulo="1. Dados do emissor">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <Campo rotulo="CNPJ" className="md:col-span-2"
            dica={consultaCnpj?.estado === "buscando" ? consultaCnpj.texto : "Digite o CNPJ: os dados são buscados na Receita Federal"}>
            <Entrada value={mascaraDocumento(e.cnpj)} inputMode="numeric" onChange={(ev) => set("cnpj", soDigitos(ev.target.value))} />
          </Campo>
          <Campo rotulo="Razão social" className="md:col-span-4"><Entrada value={e.razao_social} maxLength={60} onChange={(ev) => set("razao_social", ev.target.value)} /></Campo>
          <Campo rotulo="Nome fantasia" className="md:col-span-2"><Entrada value={e.nome_fantasia || ""} onChange={(ev) => set("nome_fantasia", ev.target.value)} /></Campo>
          <Campo rotulo="Inscrição estadual" className="md:col-span-2"><Entrada value={e.inscricao_estadual} onChange={(ev) => set("inscricao_estadual", ev.target.value)} /></Campo>
          <Campo rotulo="Inscrição municipal" className="md:col-span-2"><Entrada value={e.inscricao_municipal || ""} onChange={(ev) => set("inscricao_municipal", ev.target.value)} /></Campo>
          <Campo rotulo="Regime tributário" className="md:col-span-2"><Selecao value={e.regime_tributario} onChange={(ev) => set("regime_tributario", ev.target.value)} opcoes={REGIMES} /></Campo>
          <Campo rotulo="E-mail (avisos e vencimento do certificado)" className="md:col-span-4"><Entrada type="email" value={e.email || ""} onChange={(ev) => set("email", ev.target.value)} /></Campo>
          <Campo rotulo="CEP"><Entrada value={e.endereco.cep} maxLength={8} onChange={(ev) => setEnd("cep", soDigitos(ev.target.value))} /></Campo>
          <Campo rotulo="Logradouro" className="md:col-span-3"><Entrada value={e.endereco.logradouro} onChange={(ev) => setEnd("logradouro", ev.target.value)} /></Campo>
          <Campo rotulo="Número"><Entrada value={e.endereco.numero} onChange={(ev) => setEnd("numero", ev.target.value)} /></Campo>
          <Campo rotulo="Complemento"><Entrada value={e.endereco.complemento || ""} onChange={(ev) => setEnd("complemento", ev.target.value)} /></Campo>
          <Campo rotulo="Bairro" className="md:col-span-2"><Entrada value={e.endereco.bairro} onChange={(ev) => setEnd("bairro", ev.target.value)} /></Campo>
          <Campo rotulo="Município" className="md:col-span-2"><Entrada value={e.endereco.municipio} onChange={(ev) => setEnd("municipio", ev.target.value)} /></Campo>
          <Campo rotulo="Cód. IBGE"><Entrada value={e.endereco.codigo_municipio} maxLength={7} onChange={(ev) => setEnd("codigo_municipio", soDigitos(ev.target.value))} /></Campo>
          <Campo rotulo="UF"><Selecao value={e.endereco.uf} onChange={(ev) => setEnd("uf", ev.target.value)} opcoes={UFS.map((u) => [u, u])} /></Campo>
          <Campo rotulo="Telefone" className="md:col-span-2"><Entrada value={e.endereco.telefone || ""} onChange={(ev) => setEnd("telefone", soDigitos(ev.target.value))} /></Campo>
        </div>
              {consultaCnpj && consultaCnpj.estado !== "buscando" && (
          <div className="mt-3"><Aviso tipo={consultaCnpj.estado === "ok" ? "sucesso" : "atencao"}>{consultaCnpj.texto}</Aviso></div>
        )}
      </Cartao>
      )}

      {!prod.liberado && !prod.carregando && (
        <Aviso tipo={e.ambiente === "producao" || prod.erro ? "erro" : undefined}>
          {prod.erro ? prod.mensagem : e.ambiente === "producao"
            ? "Este emissor está em produção, mas a emissão com validade fiscal só é liberada com um plano pago ativo. Troque para homologação para continuar testando, ou escolha um plano."
            : prod.mensagem}
        </Aviso>
      )}
      <Cartao titulo={nfceLiberada ? "Emissão (NF-e e NFC-e)" : "Emissão (NF-e)"}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Campo rotulo="Ambiente" dica={prod.liberado ? "Comece em homologação; troque para produção depois dos testes"
            : prod.carregando ? "Conferindo a liberação da produção…" : "Produção liberada com um plano pago ativo"}>
            <Selecao value={e.ambiente} onChange={(ev) => set("ambiente", ev.target.value)} data-testid="fiscal-ambiente"
              opcoes={prod.liberado || e.ambiente === "producao"
                ? [["homologacao", "Homologação (testes)"], ["producao", prod.liberado ? "Produção" : prod.carregando ? "Produção (verificando…)" : "Produção (indisponível sem plano ativo)"]]
                : [["homologacao", "Homologação (testes)"]]} />
          </Campo>
          <Campo rotulo="Série NF-e"><Entrada type="number" min="0" value={e.serie_nfe} onChange={(ev) => set("serie_nfe", Number(ev.target.value))} /></Campo>
          {nfceLiberada && <Campo rotulo="Série NFC-e"><Entrada type="number" min="0" value={e.serie_nfce} onChange={(ev) => set("serie_nfce", Number(ev.target.value))} /></Campo>}
          <Campo rotulo="Grupo IBS/CBS (reforma)" dica="Automático: Lucro Presumido/Real desde 03/08/2026 (NF-e) e 01/10/2026 (NFS-e); Simples a partir de 01/01/2027">
            <Selecao value={e.incluir_ibs_cbs ? "1" : "0"} onChange={(ev) => set("incluir_ibs_cbs", ev.target.value === "1")} opcoes={[["0", "Automático pela obrigatoriedade"], ["1", "Sempre incluir"]]} />
          </Campo>
        </div>
      </Cartao>

      <Cartao titulo="NFS-e (padrão nacional)" acoes={<VerificarMunicipio ibge={e.endereco.codigo_municipio} />}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Campo rotulo="Série da DPS"><Entrada value={e.nfse_serie_dps} maxLength={5} onChange={(ev) => set("nfse_serie_dps", soDigitos(ev.target.value))} /></Campo>
          <Campo rotulo="Próximo nº de DPS" dica="Só se você já emitia em outro sistema; aplicado uma vez">
            <Entrada type="number" min="1" value={e.nfse_numero_inicial_dps} onChange={(ev) => set("nfse_numero_inicial_dps", ev.target.value)} />
          </Campo>
          <Campo rotulo="Regime especial de tributação" className="md:col-span-2">
            <Selecao value={e.nfse_regime_especial} onChange={(ev) => set("nfse_regime_especial", Number(ev.target.value))}
              opcoes={[[0, "Nenhum"], [1, "Ato cooperado (cooperativa)"], [2, "Estimativa"], [3, "Microempresa municipal"],
                [4, "Notário ou registrador"], [5, "Profissional autônomo"], [6, "Sociedade de profissionais"]]} />
          </Campo>
          {["1", "2"].includes(e.regime_tributario) && (
            <>
              <Campo rotulo="Apuração no Simples" className="md:col-span-2">
                <Selecao value={e.nfse_reg_ap_trib_sn} onChange={(ev) => set("nfse_reg_ap_trib_sn", Number(ev.target.value))}
                  opcoes={[[1, "Tributos federais e ISS pelo Simples"], [2, "Federais pelo Simples; ISS fora (sublimite)"], [3, "Federais e ISS fora do Simples"]]} />
              </Campo>
              <Campo rotulo="Alíquota efetiva do Simples (%)" dica="Sai como total aproximado de tributos">
                <Numero value={e.nfse_aliquota_sn} onChange={(v) => set("nfse_aliquota_sn", v)} />
              </Campo>
            </>
          )}
          <Campo rotulo="Incentivo fiscal">
            <Selecao value={e.nfse_incentivo_fiscal ? "1" : "0"} onChange={(ev) => set("nfse_incentivo_fiscal", ev.target.value === "1")} opcoes={[["0", "Não"], ["1", "Sim"]]} />
          </Campo>
          <Campo rotulo="Login da prefeitura" dica="Só se o provedor do seu município exigir">
            <Entrada value={e.nfse_prefeitura_login} autoComplete="off" onChange={(ev) => set("nfse_prefeitura_login", ev.target.value)} />
          </Campo>
          <Campo rotulo="Senha da prefeitura" dica={meta.nfse_prefeitura_senha_configurada ? "Já configurada. Preencha só para trocar." : undefined}>
            <Entrada type="password" value={e.nfse_prefeitura_senha} autoComplete="new-password"
              placeholder={meta.nfse_prefeitura_senha_configurada ? "••••••••" : ""} onChange={(ev) => set("nfse_prefeitura_senha", ev.target.value)} />
          </Campo>
        </div>
        {!e.inscricao_municipal && <p className="mt-2 text-xs text-amber-700">A NFS-e exige a inscrição municipal do emissor (campo em Dados do emissor).</p>}
      </Cartao>

      {!nfceLiberada ? (
        <Cartao titulo="NFC-e (cupom fiscal do PDV)">
          <div data-testid="fiscal-nfce-nao-liberada">
            {docs === null ? <p className="text-sm text-gray-500">Conferindo se a NFC-e está liberada…</p> : (
              <Aviso tipo="info">A emissão de NFC-e ainda não está liberada no sistema: a FSC Sistemas está concluindo o credenciamento
                na SEFAZ. Você não precisa preencher nada agora. Quando for liberada, este campo volta a aparecer aqui e no PDV.
                {meta.nfce_csc_configurado ? " O CSC que você já salvou continua guardado." : ""}</Aviso>)}
          </div>
        </Cartao>
      ) : <Cartao titulo="NFC-e (cupom fiscal do PDV)">
        <p className="mb-3 text-sm text-gray-600">
          Só precisa preencher se for emitir NFC-e. O CSC é um código de segurança que a SEFAZ do seu estado gera
          para a sua empresa; ele vai no QR Code do cupom. Quem só emite NF-e ou NFS-e pode deixar em branco.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Campo rotulo="ID do CSC" dica={'Número curto que a SEFAZ mostra junto do código (ex.: "1" ou "000001").'}>
            <Entrada value={e.nfce_id_csc} inputMode="numeric" onChange={(ev) => set("nfce_id_csc", soDigitos(ev.target.value))} />
          </Campo>
          <Campo rotulo="CSC (código)" className="md:col-span-3"
            dica={meta.nfce_csc_configurado ? "Já configurado. Preencha só para trocar." : "Código longo (letras e números) gerado no portal da SEFAZ. Fica guardado criptografado."}>
            <Entrada type="password" value={e.nfce_csc} autoComplete="new-password" placeholder={meta.nfce_csc_configurado ? "••••••••" : ""}
              onChange={(ev) => set("nfce_csc", ev.target.value)} />
          </Campo>
        </div>
        <details className="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
          <summary className="cursor-pointer font-medium">Como gerar o CSC na SEFAZ ({e.endereco.uf})</summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            <li>Entre no portal da SEFAZ do seu estado com o certificado digital da empresa (ou peça ao seu contador).</li>
            <li>Procure a área de NFC-e e a opção “CSC”, “Código de Segurança do Contribuinte” ou “Token NFC-e”.</li>
            <li>Gere um CSC para <strong>{e.ambiente === "producao" ? "produção" : "homologação (testes)"}</strong> — o de testes e o de produção são diferentes.</li>
            <li>Copie o <strong>ID</strong> e o <strong>código</strong> para os campos acima e clique em “Salvar dados da empresa”.</li>
            <li>Em alguns estados é preciso antes se credenciar como emissor de NFC-e no mesmo portal.</li>
          </ol>
          <a className="mt-2 inline-block text-blue-700 underline" target="_blank" rel="noopener noreferrer"
            href={`https://www.google.com/search?q=${encodeURIComponent(`gerar CSC NFC-e SEFAZ ${e.endereco.uf}`)}`}>
            Buscar o passo a passo da SEFAZ-{e.endereco.uf} ↗
          </a>
        </details>
      </Cartao>}

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Botao carregando={salvando} onClick={salvar}>{meta.fonte_empresa ? "Salvar parâmetros fiscais" : "Salvar dados da empresa"}</Botao>
      </div>
      {msg && <Aviso tipo={msg.tipo}>{msg.texto}</Aviso>}

      {caminhoCertificado()
        ? <ResumoCertificado emissorSalvo={emissorSalvo} />
        : <CertificadoDigital emissorSalvo={emissorSalvo} onAlterado={recarregarSync} />}

      <Cartao titulo="3. Integração com a SEFAZ e a prefeitura">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-600">
            <p>A FSC Sistemas cadastra sua empresa automaticamente na plataforma que transmite as notas
              (ao salvar os dados e ao enviar o certificado). Você não precisa criar conta em nenhum outro site.</p>
            <SituacaoSync meta={meta} />
          </div>
          {emissorSalvo && (
            <Botao variante="secundario" carregando={sincronizando} onClick={sincronizar}>
              {meta.sincronizacao_erro || !meta.empresa_sincronizada_em ? "Tentar de novo" : "Atualizar agora"}
            </Botao>
          )}
        </div>
      </Cartao>

      <RegrasNcm />
      <CatalogoServicos />
      <IssMunicipios />
    </div>
  );
}

// Quando o certificado é gerenciado em "Minha empresa", aqui fica só o resumo com o atalho
function ResumoCertificado({ emissorSalvo }) {
  const [st, setSt] = useState(null);
  useEffect(() => { fiscal.statusCertificado().then(setSt).catch(() => {}); }, []);
  const cor = { ativo: "text-emerald-700", vence_em_breve: "text-amber-700", expirado: "text-red-700", ausente: "text-gray-600" }[st?.status] || "";
  const rotulo = { ativo: "Ativo", vence_em_breve: "Vence em breve", expirado: "Expirado", ausente: "Não enviado" }[st?.status] || "…";
  return (
    <Cartao titulo="2. Certificado digital">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div>
          <p>Status: <strong className={cor}>{rotulo}</strong>
            {st?.valido_ate && <> · válido até <strong>{dataBR(st.valido_ate)}</strong></>}</p>
          {st?.possui_certificado && !st?.termo_versao && <p className="text-amber-700">Autorização de uso pendente.</p>}
          <p className="text-gray-500">O envio, a troca e a autorização do certificado ficam em <strong>Sistema → Minha empresa</strong>.</p>
        </div>
        <a href={caminhoCertificado()} className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-gray-50">
          {st?.possui_certificado ? "Gerenciar certificado" : "Enviar certificado"}
        </a>
      </div>
      {!emissorSalvo && <p className="mt-2 text-xs text-amber-700">Salve os dados do emissor acima antes de enviar o certificado.</p>}
    </Cartao>
  );
}

// Dados da empresa vindos de "Sistema → Minha empresa" (fonte única), somente leitura aqui
function EmpresaDaFonte({ e, pendencias }) {
  const end = e.endereco || {};
  const regime = (REGIMES.find(([v]) => v === e.regime_tributario) || [])[1] || "—";
  const faltando = (pendencias || []).filter((x) => !x.startsWith("parâmetros fiscais"));
  const linha = (rotulo, valor) => (
    <div><dt className="text-xs text-gray-500">{rotulo}</dt><dd className="font-medium">{valor || <span className="text-amber-700">não informado</span>}</dd></div>
  );
  return (
    <Cartao titulo="1. Dados da empresa"
      acoes={caminhoEmpresa() && <a href={caminhoEmpresa()} className="text-sm text-blue-700 underline">Editar em Minha empresa</a>}>
      <p className="mb-3 text-sm text-gray-600">Estes dados vêm de <strong>Sistema → Minha empresa</strong> (cadastro único).
        Para alterar, edite lá — as notas e a integração fiscal são atualizadas automaticamente.</p>
      <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-4">
        {linha("CNPJ", e.cnpj && mascaraDocumento(e.cnpj))}
        <div className="md:col-span-2">{linha("Razão social", e.razao_social)}</div>
        {linha("Nome fantasia", e.nome_fantasia)}
        {linha("Inscrição estadual", e.inscricao_estadual)}
        {linha("Inscrição municipal (NFS-e)", e.inscricao_municipal)}
        {linha("Regime tributário", regime)}
        {linha("E-mail", e.email)}
        <div className="md:col-span-3">{linha("Endereço", [end.logradouro, end.numero, end.complemento, end.bairro].filter(Boolean).join(", "))}</div>
        {linha("Município / UF", end.municipio && `${end.municipio}/${end.uf} (IBGE ${end.codigo_municipio || "?"})`)}
      </dl>
      {faltando.length > 0 && (
        <div className="mt-3"><Aviso tipo="atencao">Para emitir notas, complete em Minha empresa: {faltando.join(", ")}.</Aviso></div>
      )}
    </Cartao>
  );
}

function metaSync(d) {
  return { fonte_empresa: !!d.fonte_empresa, empresa_pendencias: d.empresa_pendencias || [],
    empresa_sincronizada_em: d.empresa_sincronizada_em, sincronizacao_erro: d.sincronizacao_erro,
    sincronizacao_tentativa_em: d.sincronizacao_tentativa_em, sincronizacao_aviso_nfse: d.sincronizacao_aviso_nfse };
}

function SituacaoSync({ meta }) {
  if (meta.sincronizacao_erro) {
    return <p className="mt-2 text-red-700">Não concluída ({dataBR(meta.sincronizacao_tentativa_em, true)}): {meta.sincronizacao_erro}.
      Corrija os dados acima e salve, ou clique em “Tentar de novo”. Se persistir, fale com o suporte da FSC.</p>;
  }
  if (meta.empresa_sincronizada_em) {
    return (
      <p className="mt-2 text-emerald-700">✓ Empresa ativa para emissão desde {dataBR(meta.empresa_sincronizada_em, true)}.
        {meta.sincronizacao_aviso_nfse && <span className="block text-amber-700">NFS-e: {meta.sincronizacao_aviso_nfse}</span>}
      </p>
    );
  }
  return <p className="mt-2 text-amber-700">Pendente: salve os dados da empresa e envie o certificado digital.</p>;
}

const REGRA_VAZIA = { ncm_prefixo: "", uf_destino: "*", descricao: "", st_modo: "", icms_mva_st: "", st_mva_ajustada: true,
  icms_aliquota: "", fcp_aliquota: "", icms_reducao_base: "", icms_cst: "", cbenef: "", cfop: "",
  credito_sn_aliquota: "", difal_base_dupla: false, ibs_cbs_class_trib: "" };
const NUMERICOS = ["icms_mva_st", "icms_aliquota", "fcp_aliquota", "icms_reducao_base", "credito_sn_aliquota"];

function RegrasNcm() {
  const [regras, setRegras] = useState([]);
  const [f, setF] = useState(REGRA_VAZIA);
  const [erro, setErro] = useState(null);
  const carregar = () => fiscal.regrasNcm().then(setRegras).catch(() => {});
  useEffect(() => { carregar(); }, []);
  const set = (c, v) => setF((x) => ({ ...x, [c]: v }));

  const salvar = async () => {
    setErro(null);
    const tributos = {};
    NUMERICOS.forEach((c) => { if (f[c] !== "") tributos[c] = Number(f[c]); });
    ["st_modo", "icms_cst", "cbenef", "cfop", "ibs_cbs_class_trib"].forEach((c) => { if (f[c]) tributos[c] = f[c]; });
    if (f.st_modo === "substituto") tributos.st_mva_ajustada = f.st_mva_ajustada;
    if (f.difal_base_dupla) tributos.difal_base_dupla = true;
    try {
      await fiscal.salvarRegraNcm({ ncm_prefixo: soDigitos(f.ncm_prefixo), uf_destino: f.uf_destino, descricao: f.descricao, tributos });
      setF(REGRA_VAZIA);
      carregar();
    } catch (e) {
      setErro(e.message);
    }
  };

  const editar = (r) => {
    const t = r.tributos || {};
    setF({ ...REGRA_VAZIA, ncm_prefixo: r.ncm_prefixo, uf_destino: r.uf_destino || "*", descricao: r.descricao || "",
      ...Object.fromEntries(Object.entries(t).map(([k, v]) => [k, v ?? ""])),
      st_modo: t.st_modo || (t.icms_st ? "retido" : "") });
  };

  return (
    <Cartao titulo="Regras de tributação por NCM e UF de destino (ajustes do contador)">
      <p className="mb-3 text-sm text-gray-600">
        Sem regra, valem a alíquota padrão da UF e as tabelas interestaduais. Cadastre aqui as exceções do produto: substituição tributária
        (com a MVA de cada estado), alíquota própria, FCP, redução de base e cBenef. A regra “Todas as UFs” vale sempre; a regra de uma UF
        específica se sobrepõe a ela quando a nota vai para aquele estado (ou é dentro do seu estado). O prefixo vale para todos os NCMs que
        começam com ele (ex.: 8708 = autopeças).
      </p>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
        <Campo rotulo="NCM ou prefixo"><Entrada value={f.ncm_prefixo} onChange={(e) => set("ncm_prefixo", soDigitos(e.target.value))} /></Campo>
        <Campo rotulo="UF de destino"><Selecao value={f.uf_destino} onChange={(e) => set("uf_destino", e.target.value)} opcoes={[["*", "Todas as UFs"], ...UFS.map((u) => [u, u])]} /></Campo>
        <Campo rotulo="Descrição" className="col-span-2 md:col-span-4"><Entrada value={f.descricao} onChange={(e) => set("descricao", e.target.value)} /></Campo>
        <Campo rotulo="Substituição tributária">
          <Selecao value={f.st_modo} onChange={(e) => set("st_modo", e.target.value)}
            opcoes={[["", "Sem ST"], ["retido", "ST já retida antes (revenda)"], ["substituto", "Emitente retém a ST"]]} />
        </Campo>
        {f.st_modo === "substituto" && (
          <>
            <Campo rotulo="% MVA original"><Numero value={f.icms_mva_st} onChange={(v) => set("icms_mva_st", v)} /></Campo>
            <Campo rotulo="Ajustar MVA (interestadual)">
              <Selecao value={f.st_mva_ajustada ? "1" : "0"} onChange={(e) => set("st_mva_ajustada", e.target.value === "1")} opcoes={[["1", "Sim"], ["0", "Não"]]} />
            </Campo>
          </>
        )}
        <Campo rotulo="% ICMS interno do produto" dica="Na UF da regra (destino)"><Numero value={f.icms_aliquota} onChange={(v) => set("icms_aliquota", v)} /></Campo>
        <Campo rotulo="% FCP do produto"><Numero value={f.fcp_aliquota} onChange={(v) => set("fcp_aliquota", v)} /></Campo>
        <Campo rotulo="% redução de base"><Numero value={f.icms_reducao_base} onChange={(v) => set("icms_reducao_base", v)} /></Campo>
        <Campo rotulo="CST ICMS fixo">
          <Selecao value={f.icms_cst} vazio="Automático" onChange={(e) => set("icms_cst", e.target.value)}
            opcoes={[["40", "40 — Isenta"], ["41", "41 — Não tributada"], ["50", "50 — Suspensão"], ["51", "51 — Diferimento"], ["90", "90 — Outras"]]} />
        </Campo>
        <Campo rotulo="cBenef"><Entrada value={f.cbenef} maxLength={10} onChange={(e) => set("cbenef", e.target.value.toUpperCase())} /></Campo>
        <Campo rotulo="CFOP fixo"><Entrada value={f.cfop} maxLength={4} placeholder="auto" onChange={(e) => set("cfop", soDigitos(e.target.value))} /></Campo>
        <Campo rotulo="% crédito Simples"><Numero value={f.credito_sn_aliquota} onChange={(v) => set("credito_sn_aliquota", v)} /></Campo>
        <Campo rotulo="cClassTrib (IBS/CBS)"><Entrada value={f.ibs_cbs_class_trib} maxLength={6} placeholder="000001" onChange={(e) => set("ibs_cbs_class_trib", soDigitos(e.target.value))} /></Campo>
        {f.uf_destino !== "*" && (
          <label className="flex items-end gap-2 pb-2 text-sm">
            <input type="checkbox" checked={f.difal_base_dupla} onChange={(e) => set("difal_base_dupla", e.target.checked)} /> DIFAL com base dupla
          </label>
        )}
        <div className="flex items-end gap-2">
          <Botao onClick={salvar} disabled={soDigitos(f.ncm_prefixo).length < 2}>Salvar regra</Botao>
          <Botao variante="secundario" onClick={() => setF(REGRA_VAZIA)}>Limpar</Botao>
        </div>
      </div>
      {erro && <div className="mt-2"><Aviso tipo="erro">{erro}</Aviso></div>}
      <div className="mt-4">
        <Tabela
          chave={(r) => `${r.ncm_prefixo}-${r.uf_destino}`}
          linhas={regras}
          vazio="Nenhuma regra própria — valem as sugestões padrão"
          colunas={[
            { id: "ncm_prefixo", rotulo: "NCM" },
            { id: "uf_destino", rotulo: "UF", render: (r) => (r.uf_destino === "*" ? "Todas" : r.uf_destino) },
            { id: "descricao", rotulo: "Descrição" },
            { id: "st", rotulo: "ST", render: (r) => {
              const t = r.tributos || {};
              if (t.st_modo === "substituto") return `Retém (MVA ${t.icms_mva_st ?? "?"}%)`;
              return t.st_modo === "retido" || t.icms_st ? "Já retida" : "—";
            } },
            { id: "icms", rotulo: "% ICMS", render: (r) => r.tributos?.icms_aliquota ?? "auto" },
            { id: "fcp", rotulo: "% FCP", render: (r) => r.tributos?.fcp_aliquota ?? "auto" },
            { id: "cbenef", rotulo: "cBenef", render: (r) => r.tributos?.cbenef || "—" },
            { id: "x", rotulo: "", render: (r) => (
              <span className="whitespace-nowrap">
                <Botao variante="fantasma" onClick={() => editar(r)}>Editar</Botao>
                <Botao variante="fantasma" className="text-red-600" onClick={() => fiscal.removerRegraNcm(r.ncm_prefixo, r.uf_destino).then(carregar)}>Remover</Botao>
              </span>
            ) },
          ]}
        />
      </div>
    </Cartao>
  );
}

function VerificarMunicipio({ ibge }) {
  const [r, setR] = useState(null);
  const verificar = async () => {
    try {
      const m = await fiscal.municipioNfse(ibge);
      setR({ ok: true, texto: `Atendido${m.provedor ? ` · provedor ${m.provedor}` : ""}` });
    } catch (err) {
      setR({ ok: false, texto: err.status === 404 ? "Município não atendido" : err.message });
    }
  };
  return (
    <span className="flex items-center gap-2 text-xs">
      {r && <span className={r.ok ? "text-emerald-700" : "text-red-700"}>{r.texto}</span>}
      <Botao variante="fantasma" disabled={!ibge || ibge.length !== 7} onClick={verificar}>Verificar município</Botao>
    </span>
  );
}

const SERVICO_VAZIO = { codigo: "", descricao: "", codigo_tributacao_nacional: "", codigo_tributacao_municipal: "", nbs: "",
  cnae: "", aliquota_iss: 0, iss_retido: false, valor_padrao: 0, incidencia_local_prestacao: false, permite_deducao: false,
  ibs_cbs_cst: "000", ibs_cbs_class_trib: "000001", indicador_operacao: "" };

function CatalogoServicos() {
  const [lista, setLista] = useState([]);
  const [f, setF] = useState(SERVICO_VAZIO);
  const [editando, setEditando] = useState(null);
  const [erro, setErro] = useState(null);
  const carregar = () => fiscal.servicos().then(setLista).catch(() => {});
  useEffect(() => { carregar(); }, []);
  const set = (c, v) => setF((x) => ({ ...x, [c]: v }));

  const salvar = async () => {
    setErro(null);
    const corpo = { ...f, codigo_tributacao_municipal: f.codigo_tributacao_municipal || null, nbs: f.nbs || null, cnae: f.cnae || null,
      indicador_operacao: f.indicador_operacao || null };
    try {
      if (editando) await fiscal.atualizarServico(editando, corpo);
      else await fiscal.criarServico(corpo);
      setF(SERVICO_VAZIO);
      setEditando(null);
      carregar();
    } catch (e) {
      setErro(e.message);
    }
  };

  return (
    <Cartao titulo="Catálogo de serviços (NFS-e)">
      <p className="mb-3 text-sm text-gray-600">
        Cadastre os serviços que você presta. Na emissão, basta escolher o serviço: código de tributação, NBS e alíquota do ISS são preenchidos sozinhos.
      </p>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
        <Campo rotulo="Código"><Entrada value={f.codigo} onChange={(e) => set("codigo", e.target.value)} /></Campo>
        <Campo rotulo="Descrição" className="col-span-2 md:col-span-3"><Entrada value={f.descricao} onChange={(e) => set("descricao", e.target.value)} /></Campo>
        <Campo rotulo="Cód. trib. nacional" dica="6 dígitos"><Entrada value={f.codigo_tributacao_nacional} maxLength={8} onChange={(e) => set("codigo_tributacao_nacional", soDigitos(e.target.value))} /></Campo>
        <Campo rotulo="Cód. trib. municipal"><Entrada value={f.codigo_tributacao_municipal} onChange={(e) => set("codigo_tributacao_municipal", e.target.value)} /></Campo>
        <Campo rotulo="NBS"><Entrada value={f.nbs} maxLength={9} onChange={(e) => set("nbs", soDigitos(e.target.value))} /></Campo>
        <Campo rotulo="CNAE"><Entrada value={f.cnae} onChange={(e) => set("cnae", soDigitos(e.target.value))} /></Campo>
        <Campo rotulo="% ISS"><Numero value={f.aliquota_iss} onChange={(v) => set("aliquota_iss", Number(v))} /></Campo>
        <Campo rotulo="ISS retido?"><Selecao value={f.iss_retido ? "1" : "0"} onChange={(e) => set("iss_retido", e.target.value === "1")} opcoes={[["0", "Não"], ["1", "Sim"]]} /></Campo>
        <Campo rotulo="Valor padrão"><Valor value={f.valor_padrao} onChange={(v) => set("valor_padrao", v)} /></Campo>
        <Campo rotulo="Onde o ISS é devido" className="col-span-2" dica="Exceções do art. 3º da LC 116: obra, limpeza, vigilância, mão de obra…">
          <Selecao value={f.incidencia_local_prestacao ? "1" : "0"} onChange={(e) => set("incidencia_local_prestacao", e.target.value === "1")}
            opcoes={[["0", "No meu município (regra geral)"], ["1", "No local da prestação (exceção art. 3º)"]]} />
        </Campo>
        <Campo rotulo="Permite dedução?" dica="Construção civil"><Selecao value={f.permite_deducao ? "1" : "0"} onChange={(e) => set("permite_deducao", e.target.value === "1")} opcoes={[["0", "Não"], ["1", "Sim"]]} /></Campo>
        <Campo rotulo="cIndOp (IBS/CBS)" dica="Indicador de operação, 6 dígitos"><Entrada value={f.indicador_operacao || ""} maxLength={6} onChange={(e) => set("indicador_operacao", soDigitos(e.target.value))} /></Campo>
        <Campo rotulo="CST IBS/CBS"><Entrada value={f.ibs_cbs_cst} maxLength={3} onChange={(e) => set("ibs_cbs_cst", soDigitos(e.target.value))} /></Campo>
        <Campo rotulo="cClassTrib"><Entrada value={f.ibs_cbs_class_trib} maxLength={6} onChange={(e) => set("ibs_cbs_class_trib", soDigitos(e.target.value))} /></Campo>
        <div className="flex items-end gap-2">
          <Botao onClick={salvar} disabled={!f.codigo || f.descricao.length < 3 || f.codigo_tributacao_nacional.length !== 6}>{editando ? "Atualizar" : "Adicionar"}</Botao>
          {editando && <Botao variante="secundario" onClick={() => { setEditando(null); setF(SERVICO_VAZIO); }}>Cancelar</Botao>}
        </div>
      </div>
      {erro && <div className="mt-2"><Aviso tipo="erro">{erro}</Aviso></div>}
      <div className="mt-4">
        <Tabela
          chave={(r) => r.id}
          linhas={lista}
          vazio="Nenhum serviço cadastrado"
          colunas={[
            { id: "codigo", rotulo: "Código" },
            { id: "descricao", rotulo: "Descrição" },
            { id: "codigo_tributacao_nacional", rotulo: "Cód. nacional" },
            { id: "aliquota_iss", rotulo: "% ISS", direita: true },
            { id: "iss_retido", rotulo: "Retido", render: (r) => (r.iss_retido ? "Sim" : "Não") },
            { id: "local", rotulo: "ISS devido", render: (r) => (r.incidencia_local_prestacao ? "Local da prestação" : "Meu município") },
            { id: "ibs", rotulo: "IBS/CBS", render: (r) => (r.indicador_operacao ? "Configurado" : <span className="text-amber-700">Falta cIndOp</span>) },
            { id: "x", rotulo: "", render: (r) => (
              <span className="whitespace-nowrap">
                <Botao variante="fantasma" onClick={() => { setEditando(r.id); setF({ ...SERVICO_VAZIO, ...r, codigo_tributacao_municipal: r.codigo_tributacao_municipal || "", nbs: r.nbs || "", cnae: r.cnae || "", indicador_operacao: r.indicador_operacao || "" }); }}>Editar</Botao>
                <Botao variante="fantasma" className="text-red-600" onClick={() => fiscal.removerServico(r.id).then(carregar)}>Remover</Botao>
              </span>
            ) },
          ]}
        />
      </div>
    </Cartao>
  );
}

const MUN_VAZIO = { codigo_municipio: "", nome: "", uf: "SC", aliquota_padrao: "", retencao: "nunca", aliquotas_texto: "" };

function IssMunicipios() {
  const [lista, setLista] = useState([]);
  const [f, setF] = useState(MUN_VAZIO);
  const [erro, setErro] = useState(null);
  const carregar = () => fiscal.issMunicipios().then(setLista).catch(() => {});
  useEffect(() => { carregar(); }, []);
  const set = (c, v) => setF((x) => ({ ...x, [c]: v }));

  // "0702=4; 1701=2,5" → {"0702": 4, "1701": 2.5}
  const lerAliquotas = (txt) => Object.fromEntries(
    txt.split(/[;\n]/).map((p) => p.split("=").map((x) => x.trim())).filter(([k, v]) => k && v)
      .map(([k, v]) => [soDigitos(k), Number(v.replace(",", "."))]),
  );
  const salvar = async () => {
    setErro(null);
    try {
      await fiscal.salvarIssMunicipio({ codigo_municipio: soDigitos(f.codigo_municipio), nome: f.nome, uf: f.uf,
        aliquota_padrao: f.aliquota_padrao === "" ? null : Number(f.aliquota_padrao), retencao: f.retencao,
        aliquotas_por_servico: lerAliquotas(f.aliquotas_texto) });
      setF(MUN_VAZIO);
      carregar();
    } catch (e) {
      setErro(e.message);
    }
  };
  const editar = (m) => setF({ ...MUN_VAZIO, ...m, aliquota_padrao: m.aliquota_padrao ?? "",
    aliquotas_texto: Object.entries(m.aliquotas_por_servico || {}).map(([k, v]) => `${k}=${v}`).join("; ") });

  return (
    <Cartao titulo="ISS por município (NFS-e)">
      <p className="mb-3 text-sm text-gray-600">
        Usada para sugerir a alíquota quando o ISS é devido em outro município (exceções do art. 3º da LC 116) e para saber se o
        tomador daquele município é obrigado a reter o ISS. Cadastre seu próprio município e os municípios onde você presta serviço.
        Alíquotas por serviço: código de tributação (ou prefixo) = alíquota, separados por ponto e vírgula. Ex.: <code>0702=4; 1701=2,5</code>
      </p>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
        <Campo rotulo="Código IBGE"><Entrada value={f.codigo_municipio} maxLength={7} onChange={(e) => set("codigo_municipio", soDigitos(e.target.value))} /></Campo>
        <Campo rotulo="Município" className="md:col-span-2"><Entrada value={f.nome} onChange={(e) => set("nome", e.target.value)} /></Campo>
        <Campo rotulo="UF"><Selecao value={f.uf} onChange={(e) => set("uf", e.target.value)} opcoes={UFS.map((u) => [u, u])} /></Campo>
        <Campo rotulo="% ISS padrão"><Numero value={f.aliquota_padrao} onChange={(v) => set("aliquota_padrao", v)} /></Campo>
        <Campo rotulo="Retenção pelo tomador">
          <Selecao value={f.retencao} onChange={(e) => set("retencao", e.target.value)}
            opcoes={[["nunca", "Não exige"], ["excecoes_art3", "Nos serviços do art. 3º"], ["sempre_pj", "Sempre, tomador PJ"]]} />
        </Campo>
        <Campo rotulo="Alíquotas por serviço" className="col-span-2 md:col-span-5"><Entrada value={f.aliquotas_texto} placeholder="0702=4; 1701=2,5" onChange={(e) => set("aliquotas_texto", e.target.value)} /></Campo>
        <div className="flex items-end gap-2">
          <Botao onClick={salvar} disabled={soDigitos(f.codigo_municipio).length !== 7 || f.nome.length < 2}>Salvar</Botao>
        </div>
      </div>
      {erro && <div className="mt-2"><Aviso tipo="erro">{erro}</Aviso></div>}
      <div className="mt-4">
        <Tabela
          chave={(m) => m.codigo_municipio}
          linhas={lista}
          vazio="Nenhum município cadastrado"
          colunas={[
            { id: "nome", rotulo: "Município", render: (m) => `${m.nome}/${m.uf}` },
            { id: "codigo_municipio", rotulo: "IBGE" },
            { id: "aliquota_padrao", rotulo: "% padrão", render: (m) => m.aliquota_padrao ?? "—" },
            { id: "ap", rotulo: "Por serviço", render: (m) => Object.entries(m.aliquotas_por_servico || {}).map(([k, v]) => `${k}: ${v}%`).join(", ") || "—" },
            { id: "retencao", rotulo: "Retenção", render: (m) => ({ nunca: "Não exige", excecoes_art3: "Art. 3º", sempre_pj: "Sempre PJ" }[m.retencao]) },
            { id: "x", rotulo: "", render: (m) => (
              <span className="whitespace-nowrap">
                <Botao variante="fantasma" onClick={() => editar(m)}>Editar</Botao>
                <Botao variante="fantasma" className="text-red-600" onClick={() => fiscal.removerIssMunicipio(m.codigo_municipio).then(carregar)}>Remover</Botao>
              </span>
            ) },
          ]}
        />
      </div>
    </Cartao>
  );
}
